import React, { useMemo } from 'react';

interface Holiday {
  date: string;
  description?: string;
  event?: string;
}

interface LeaveContextCalendarProps {
  startDate: string;
  endDate: string;
  holidays: Holiday[];
}

type CalendarCell = {
  date: Date;
  isLeave: boolean;
  isWeekend: boolean;
  holiday?: Holiday;
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const toDayKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeDate = (value: string) => {
  const datePart = value.includes('T') ? value.split('T')[0] : value;
  return new Date(`${datePart}T00:00:00`);
};

const shiftDate = (date: Date, days: number) =>
  new Date(date.getTime() + days * ONE_DAY_MS);

const getWeekStartMonday = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const distanceToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + distanceToMonday);
  return d;
};

const getWeekEndSunday = (date: Date) => {
  const start = getWeekStartMonday(date);
  return shiftDate(start, 6);
};

const isBetweenInclusive = (date: Date, start: Date, end: Date) =>
  date.getTime() >= start.getTime() && date.getTime() <= end.getTime();

const formatMonthYear = (date: Date) =>
  date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });

export const LeaveContextCalendar = ({
  startDate,
  endDate,
  holidays
}: LeaveContextCalendarProps) => {
  const start = useMemo(() => normalizeDate(startDate), [startDate]);
  const end = useMemo(() => normalizeDate(endDate), [endDate]);

  const holidayMap = useMemo(() => {
    const map = new Map<string, Holiday>();
    holidays.forEach((holiday) => {
      if (!holiday?.date) return;
      const key = holiday.date.split('T')[0];
      map.set(key, holiday);
    });
    return map;
  }, [holidays]);

  const contextWindow = useMemo(() => {
    const contextStart = shiftDate(start, -7);
    const contextEnd = shiftDate(end, 7);
    return { contextStart, contextEnd };
  }, [start, end]);

  const cells = useMemo(() => {
    const gridStart = getWeekStartMonday(contextWindow.contextStart);
    const gridEnd = getWeekEndSunday(contextWindow.contextEnd);
    const result: CalendarCell[] = [];

    for (
      let cursor = new Date(gridStart);
      cursor.getTime() <= gridEnd.getTime();
      cursor = shiftDate(cursor, 1)
    ) {
      const day = new Date(cursor);
      const dayKey = toDayKey(day);
      const weekday = day.getDay();
      const isWeekend = weekday === 0 || weekday === 6;

      result.push({
        date: day,
        isLeave: isBetweenInclusive(day, start, end),
        isWeekend,
        holiday: holidayMap.get(dayKey)
      });
    }

    return result;
  }, [contextWindow.contextEnd, contextWindow.contextStart, end, holidayMap, start]);

  const periodLabel = useMemo(() => {
    const first = formatMonthYear(contextWindow.contextStart);
    const last = formatMonthYear(contextWindow.contextEnd);
    return first === last ? first : `${first} - ${last}`;
  }, [contextWindow.contextEnd, contextWindow.contextStart]);

  return (
    <div className="leave-context-calendar">
      <div className="leave-context-calendar__meta">
        <div className="leave-context-calendar__period">{periodLabel}</div>
      </div>

      <div className="leave-context-calendar__grid">
        <div className="leave-context-calendar__header">
          {DAY_LABELS.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </div>
        <div className="leave-context-calendar__days">
          {cells.map((cell) => {
            const classNames = [
              'leave-context-calendar__day',
              cell.isLeave ? 'is-leave' : '',
              cell.isWeekend ? 'is-weekend' : '',
              cell.holiday ? 'is-holiday' : ''
            ]
              .filter(Boolean)
              .join(' ');

            const title = cell.holiday
              ? `${cell.holiday.description || 'Holiday'}${
                  cell.holiday.event ? ` - ${cell.holiday.event}` : ''
                }`
              : cell.isLeave
                ? 'Leave day'
                : cell.isWeekend
                  ? 'Weekend'
                  : '';

            return (
              <div key={toDayKey(cell.date)} className={classNames} title={title}>
                <span className="day-number">{cell.date.getDate()}</span>
                {cell.holiday && <span className="marker marker--holiday">H</span>}
                {cell.isLeave && <span className="marker marker--leave">L</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div className="leave-context-calendar__legend">
        <span>
          <i className="legend-swatch leave" />
          Leave
        </span>
        <span>
          <i className="legend-swatch weekend" />
          Weekend
        </span>
        <span>
          <i className="legend-swatch holiday" />
          Mercantile holiday
        </span>
      </div>
    </div>
  );
};

